import { useRef, useEffect } from 'react';
import { debounce } from 'lodash-es';

export interface DebounceOptions {
  wait?: number;
  leading?: boolean;
  trailing?: boolean;
}

export function useCreation<T>(factory: () => T, deps: any[]) {
  const { current } = useRef({
    deps,
    obj: undefined as undefined | T,
    initialized: false,
  });
  if (!current.initialized || !depsAreSame(current.deps, deps)) {
    current.deps = deps;
    current.obj = factory();
    current.initialized = true;
  }
  return current.obj as T;
}

function depsAreSame(oldDeps: any[], deps: any[]): boolean {
  if (oldDeps === deps) {
    return true;
  }
  for (let i = 0; i < oldDeps.length; i++) {
    if (oldDeps[i] !== deps[i]) {
      return false;
    }
  }
  return true;
}

type Fn = (...args: any) => any;

function useDebounceFn<T extends Fn>(
  fn: T,
  options?: DebounceOptions | number,
) {
  const fnRef = useRef<T>(fn);
  fnRef.current = fn;
  const opt: DebounceOptions | undefined =
    typeof options === 'number' ? { wait: options } : options;

  const wait = opt?.wait ?? 1000;

  const debounced = useCreation(
    () =>
      debounce<T>(((...args: any[]) => fnRef.current(...args)) as T, wait, opt),
    [],
  );

  useEffect(
    () => () => {
      debounced.cancel();
    },
    [],
  );

  return {
    run: debounced as unknown as T,
    cancel: debounced.cancel,
    flush: debounced.flush,
  };
}

export default useDebounceFn;
